import {
  Box,
  Button,
  InputLabel,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { SuppliersContext } from 'Context/SuppliersContext';
import { TherapySuppliersContext } from 'Context/SuppliersContext/TherapySuppliersContext';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { useLoader } from 'Hooks/useLoader';
import { useContext } from 'react';

interface ITagInputs {
  filterTags: { label: string; value: number }[];
  isActive: (tag: { label: string; value: number }) => boolean;
  toggleFilter: (tag: { label: string; value: number }) => void;
  resetTags: () => void;
  ariaDescription?: string;
}

export const TagsInput = ({
  filterTags,
  isActive,
  toggleFilter,
  resetTags,
  ariaDescription,
}: ITagInputs) => {
  const { userLocation, initUserLocation } = useContext(SuppliersContext);
  const { filters, setFilters } = useContext(TherapySuppliersContext);

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.Suppliers).resultPage;

  const { loader, isLoading, setIsLoading } = useLoader(
    '30',
    { height: '40px', width: 'fit-content', ml: '100px' },
    30,
    false
  );

  const locationHandler = () => {
    const updatedFilters = {
      ...filters,
      location: filters.location ? null : userLocation,
    };
    setFilters(updatedFilters);
  };

  const requestLocationHandler = async () => {
    setIsLoading(true);
    try {
      await initUserLocation();
    } finally {
      setIsLoading(false);
      locationHandler();
    }
  };

  return (
    <Stack gap={'24px'}>
      {ariaDescription && (
        <span className='sr-only' id='tags-description'>
          {ariaDescription}
        </span>
      )}
      <Stack
        direction={'row'}
        gap={'16px 6px'}
        flexWrap={'wrap'}
        component={'ul'}
        padding={0}
        margin={0}
        aria-describedby={ariaDescription && 'tags-description'}
      >
        {filterTags.map((tag, index) => (
          <ListItem sx={{ padding: 0, width: 'fit-content' }} key={index}>
            <Button
              variant='primary2'
              onClick={() => toggleFilter(tag)}
              sx={{
                padding: '0px 15px',
                height: isDesktop ? '38px' : '32px',
                outline: (theme) =>
                  isActive(tag)
                    ? 'none'
                    : `1px solid ${theme.palette.primary.shade_100}`,
                background: (theme) =>
                  isActive(tag) ? theme.palette.primary.main : '#fff',
                color: (theme) =>
                  isActive(tag) ? '#fff' : theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: (theme) =>
                    isActive(tag) ? theme.palette.primary.main : '#fff',
                },
              }}
            >
              <InputLabel
                id={`label-chip-${tag.label.trim().replace(' ', '-')}`}
              >
                <Typography
                  variant='body6'
                  color={isActive(tag) ? '#fff' : 'primary.main'}
                  sx={{
                    cursor: 'pointer',
                  }}
                >
                  {tag.label}
                </Typography>
              </InputLabel>
            </Button>
          </ListItem>
        ))}
        {isLoading ? (
          loader
        ) : (
          <Button
            variant='primary2'
            role={'button'}
            aria-pressed={userLocation ? true : false}
            aria-labelledby={dictionary.closestLocation}
            sx={{
              width: 'fit-content',
              padding: '0px 15px',
              height: isDesktop ? '38px' : '32px',
              outline: `1px solid #E7EDFF`,
              color: filters.location ? '#fff' : 'primary.main',
              bgcolor: filters.location ? 'primary.main' : '#fff',
              ':hover': {
                bgcolor: filters.location ? 'primary.main' : '#fff',
              },
            }}
            onClick={() =>
              userLocation ? locationHandler() : requestLocationHandler()
            }
          >
            <Typography variant='body6'>
              {dictionary.closestLocation}
              {!userLocation && (
                <Box
                  component='span'
                  sx={{
                    width: 'fit-content',
                    ml: '5px',
                    color: '#E12D2D',
                  }}
                >
                  {dictionary.notActive}
                </Box>
              )}
            </Typography>
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
